export interface LocationData {
  country?: { title?: string; abbreviation?: string; id?: number };
  state?: { title?: string; abbreviation?: string; id?: number };
  city?: string;
  address1?: string;
}

export interface RolePermissionMapper {
  id: number;
  title: string;
  inheritedIds: string;
}

export interface MasterDBResV2<T> {
  totalRecords: number;
  currentPage: number;
  pageSize: number;
  nextPage: string;
  prevPage: string;
  results: T;
}

export interface ClubDetailsAPIResponse {
  Title: string;
  Logo: string;
  ClubGUID: string;
  LocationOfClub_Venue: string;
  LocationOfClub_StreetAddress: string;
  LocationOfClub_City: string;
  LocationOfClub_StateTitle: string;
  LocationOfClub_Zip: string;
  AllowMembers: boolean;
  DisplayContactInfo: boolean;
  WebsiteURL: string;
  LeagueCount: number;
  TourneyCount: number;
  MemberCount: number;
}

export interface ClubLocation {
  Venue: string;
  StreetAddress: string;
  City: string;
  StateTitle: string;
  Zip: string;
  CountryTitle: string;
  BallUsed: string;
  NetTypeTitle: string;
  SurfaceTypeTitle: string;
  NumberOfCourts: number;
  IsParkingFee: boolean;
  ParkingInformation: string;
}

export interface ClubDetails {
  headerData: {
    Title: string;
    Location: string;
    ImageURL: string;
  };
  sidebarData: {
    AllowMembers: boolean;
    DisplayContactInfo: boolean;
    WebsiteURL: string;
    LeagueCount: number;
    TourneyCount: number;
    MemberCount: number;
  };
  locations: ClubLocation[];
}

export type TimezonePair = {
  value: string;
  label: string;
};

export enum RankingStatusBadgeEnum {
  TOUR_PRO = 'TOUR PRO',
  LEGEND = 'LEGEND',
  ICON = 'ICON',
  TITAN = 'TITAN',
  BALLER = 'BALLER',
  CONTENDER = 'CONTENDER',
  PLAYER = 'PLAYER',
  TOP_1 = 'Top 1',
  TOP_50 = 'Top 50',
  TOP_100 = 'Top 100',
  TOP_500 = 'Top 500',
  TOP_1000 = 'Top 1000',
  DEFAULT = '',
}

export type FormatType = 'date' | 'time';
export type TimePrecision = 'min' | 'sec';

export interface FormatDateTimeOptions {
  utcOffset?: number;
  timezoneAbbreviation?: string;
  type?: FormatType;
  precision?: TimePrecision;
  includeWeekday?: boolean;
  includeYear?: boolean;
}

export interface SearchParams {
  [key: string]: string | string[] | undefined;
}

/**
 * Numeric `SystemModuleType` values mirroring the BE proto enum. Kept in sync across `apps/pickleball`,
 * `apps/tournament`, `apps/clubs`, and `apps/team-leagues` so the `module_for` field on engagement /
 * impression payloads (and any other cross-module reference) is interpretable everywhere. When BE adds a
 * new module surface, add it to all four files together.
 */
export enum SystemModuleType {
  UNDEFINED_SYSTEM_MODULE_TYPE = 0,
  HPM_TOURS_SERIES_COLUMN = 1,
  HPM_ASSOCIATIONS_COLUMN = 2,
  HPM_PARTNERS_COLUMN = 3,
  TOURNAMENT_SYSTEM_MODULE_TYPE = 4,
  ORGANIZATION_SYSTEM_MODULE_TYPE = 11,
  LEAGUE_SYSTEM_MODULE_TYPE = 12,
  GLOBAL_SYSTEM_MODULE_TYPE = 109,
  TEAM_LEAGUES_SYSTEM_MODULE_TYPE = 120,
  TEAM_LEAGUE_SEASONS_MODULE_TYPE = 127,
  TEAM_LEAGUE_DISTRICT_SYSTEM_MODULE_TYPE = 136,
  TEAM_LEAGUE_TEAMS_SYSTEM_MODULE_TYPE = 138,
  TEAM_LEAGUE_DIVISIONS_SYSTEM_MODULE_TYPE = 141,
  CLUB_V1_SYSTEM_MODULE_TYPE = 148,
  RECOMMENDED_TOURNAMENT = 158,
  FEATURED_TOURNAMENT = 159,
  COURT_RESERVATION = 161,
  PACKAGE_RESERVATION_MODULE_TYPE = 162,
  PICKUP_GAME_SYSTEM_MODULE_TYPE = 192,
  RESERVATION_EVENT_MODULE_TYPE = 215,
  SEARCH_CARD_AD_MODULE_TYPE = 221,
  PLAYER_DASHBOARD_SYSTEM_MODULE_TYPE = 222,
  PICKLEBALL_CENTRAL_MODULE_TYPE = 223,
  PICKLEBALL_PLUS_MODULE_TYPE = 224,
  PB_PLUS_1965_CLUB_MODULE_TYPE = 227,
  UNRECOGNIZED = -1,
}

/**
 * `hp_marketing_type` values used to query the BE's `hp_marketing` endpoint. `SEARCH_CARD_AD_MODULE_TYPE = 221`
 * is the rotating search-card ad placement consumed by `<HpMarketingAd>`.
 */
export enum HomePageMarketingType {
  HPM_UNDEFINED_COLUMN = 0,
  HPM_TOURS_SERIES_COLUMN = 1,
  HPM_ASSOCIATIONS_COLUMN = 2,
  HPM_PARTNERS_COLUMN = 3,
  SEARCH_CARD_AD_MODULE_TYPE = 221,
  UNRECOGNIZED = -1,
}
